import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, merge, of, timer } from 'rxjs';
import {
  catchError,
  filter,
  groupBy,
  ignoreElements,
  mergeMap,
  switchMap,
  take,
  throttleTime,
} from 'rxjs/operators';

import { ContentStoreDispatcher } from '@common/dispatchers/content.dispatcher';
import { SettingsStoreSelector } from '@common/selectors/settings.selector';
import { UpsellingStoreSelector } from '@common/selectors/upselling.selector';
import { CartItemKeys } from '@config/keys/cart.keys';
import { ProductKeys } from '@config/keys/product.keys';
import { ICartItem } from '@core/models/cart.model';
import { DoodProductModel } from '@core/models/product.model';
import { DoodShopModel } from '@core/models/shop.model';
import { CartService } from '@core/services/cart/cart.service';
import { ShopsService } from '@core/services/shops/shops.service';
import { ModalsService } from '@core/services/modals/modals.service';
import { navigateToShopPage } from '@core/services/navigate/navigate-to-shop-page';
import { Store } from '@ngrx/store';
import {
  ProductAddModalComponent,
  ProductAddModalConfig,
} from '@shared/modals/product-add-modal/product-add-modal.component';
import { selectBogofData } from '@store/bogof/bogof.selector';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product/product.action';
import { SELECT_SHOP_ACTIVE, SELECT_SHOP_ENTITIES } from '@store/shop';
import { UPSELLING_STORE_COMPONENT_ACTIONS } from '@store/upselling/upselling.action';
import { KioskProductAddModalConfig } from '@shared/modals/kiosk-product-add-modal/kiosk-product-add-modal.component';
import { ProductOrigin, ProductOriginWithAddedValue } from '@core/models/product-origin.model';
import { nonComposableProductFreeBogofProductToAdd } from './product-bogof.util';
import { AutoFillCartItemRulesAdditionsGroup } from '@shared/local-store/cart/auto-fill-cart-item-rules/auto-fill-cart-item-rules-additions-group';

export type { BogofSuggestionForFreeCount } from './product-bogof.util';
export { nonComposableProductFreeBogofProductToAdd } from './product-bogof.util';

@Injectable({
  providedIn: 'root',
})
export class ProductEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly upsellingStoreSelector = inject(UpsellingStoreSelector);
  private readonly settingsStoreSelector = inject(SettingsStoreSelector);
  private readonly cartService = inject(CartService);
  private readonly shopsService = inject(ShopsService);
  private readonly contentDispatcher = inject(ContentStoreDispatcher);
  private readonly modalsService =
    inject<ModalsService<[ProductAddModalConfig, KioskProductAddModalConfig]>>(ModalsService);
  private readonly navigateToShopPage = inject(navigateToShopPage);

  selectProductEffect = createEffect(() => {
    const selectProductActions$ = this.actions$.pipe(
      ofType(PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct),
    );

    // Direct kiosk adds can burst from one card tap. Throttle those per product.
    // A product that opens additions or an upsell must not be dropped.
    const kioskDirectAdd$ = selectProductActions$.pipe(
      filter(({ productId, productShopId, productOrigin }) =>
        this.shouldThrottleKioskDirectAdd(productId, productShopId, productOrigin),
      ),
      groupBy(({ productId }) => productId, { duration: () => timer(300) }),
      mergeMap(productClicks$ =>
        productClicks$.pipe(throttleTime(300, undefined, { leading: true, trailing: false })),
      ),
      mergeMap(({ productId, productShopId, productOrigin }) =>
        this.processSelectProductAction(productId, productShopId, productOrigin),
      ),
    );

    const otherSelects$ = selectProductActions$.pipe(
      filter(
        ({ productId, productShopId, productOrigin }) =>
          !this.shouldThrottleKioskDirectAdd(productId, productShopId, productOrigin),
      ),
      switchMap(({ productId, productShopId, productOrigin }) =>
        this.processSelectProductAction(productId, productShopId, productOrigin),
      ),
    );

    return merge(kioskDirectAdd$, otherSelects$);
  });

  private shouldThrottleKioskDirectAdd(
    productId: string,
    productShopId: string,
    productOrigin: ProductOrigin,
  ): boolean {
    if (productOrigin.channel !== 'catalog' || !this.settingsStoreSelector.isKioskLayout()) {
      return false;
    }

    const proposals = this.upsellingStoreSelector.selectProposalsByTriggerProductId(productId)();
    if (proposals.length > 0) {
      return false;
    }

    const shop = this.shopForProduct(productShopId);
    const product = shop?.products?.find((item: { id: string }) => item.id === productId);
    if (!shop || !product) {
      return false;
    }

    return this.kioskDirectAdditionIds(product, shop) !== null;
  }

  /**
   * Null when the kiosk must open the composition screen.
   * An empty list is a simple product with nothing to choose.
   * A filled list is a product whose every addition group is a single mandatory item.
   */
  private kioskDirectAdditionIds(product: DoodProductModel, shop: DoodShopModel): string[] | null {
    if (product.type !== 'SIMPLE') {
      return null;
    }
    const additionIds = product.additions?.products ?? [];
    if (additionIds.length === 0) {
      return [];
    }
    return AutoFillCartItemRulesAdditionsGroup.exclusiveAutoFilledAdditionIdsForProduct({
      product,
      shopProducts: shop.products,
      additionGroups: shop.addition_groups,
    });
  }

  /**
   * The cart page keeps shops in the store without an active shop.
   * A refresh clears the active shop, so the product shop id is the lookup key.
   */
  private shopForProduct(productShopId: string): DoodShopModel | null {
    const entities = this.store.selectSignal(SELECT_SHOP_ENTITIES)();
    const shopById = productShopId ? entities?.[productShopId] : undefined;
    if (shopById) {
      return shopById;
    }

    const activeShop = this.store.selectSignal(SELECT_SHOP_ACTIVE)();
    if (activeShop && (!productShopId || activeShop.id === productShopId)) {
      return activeShop;
    }

    return null;
  }

  private processSelectProductAction(
    productId: string,
    productShopId: string,
    productOrigin: ProductOrigin,
  ) {
    const proposals = this.upsellingStoreSelector.selectProposalsByTriggerProductId(productId)();
    if (
      proposals.length > 0 &&
      ('catalog' === productOrigin.channel ||
        'suggestion' === productOrigin.channel ||
        'suggestion_cart' === productOrigin.channel)
    ) {
      return of(
        UPSELLING_STORE_COMPONENT_ACTIONS.showUpsellingProposals({
          triggerProductId: productId,
        }),
      );
    }

    return this.processProductSelection(productId, productShopId, productOrigin);
  }

  private processProductSelection(
    productId: string,
    productShopId: string,
    productOrigin: ProductOrigin,
  ) {
    const shop = this.shopForProduct(productShopId);
    if (!shop) {
      return this.shopsService.loadShopById$(productShopId).pipe(
        take(1),
        switchMap(loadedShop =>
          loadedShop ? this.applyProductSelection(loadedShop, productId, productOrigin) : EMPTY,
        ),
        catchError(() => EMPTY),
      );
    }

    return this.applyProductSelection(shop, productId, productOrigin);
  }

  private applyProductSelection(
    shop: DoodShopModel,
    productId: string,
    productOrigin: ProductOrigin,
  ) {
    const product = shop.products?.find((p: { id: string }) => p.id === productId);
    if (!product) {
      return EMPTY;
    }

    const directAdditionIds = this.kioskDirectAdditionIds(product, shop);
    // A simple gift has nothing to configure. Add it as its own line on every layout,
    // otherwise the marketplace reopens the product modal and the paid line absorbs it.
    if (directAdditionIds?.length === 0 && productOrigin.channel === 'bogof') {
      return this.addSimpleProductWithBogofIfEligible(product, shop, productOrigin);
    }

    if (!this.settingsStoreSelector.isKioskLayout()) {
      this.openProductAddModal({ product, shopId: shop.id });
      return EMPTY;
    }

    if (directAdditionIds) {
      return this.addSimpleProductOnly(product, shop, directAdditionIds);
    }

    if (productOrigin.channel === 'loyalty') {
      this.openKioskProductAddModal({ product, shopId: shop.id });
      return EMPTY;
    } else if (shop) {
      this.navigateToKioskProduct(shop, product);
      return EMPTY;
    }
    return EMPTY;
  }

  /** Add one simple product onto the matching cart line. */
  private addSimpleProductOnly(
    product: DoodProductModel,
    shop: DoodShopModel,
    additions: string[] = [],
  ) {
    const newCartItem = {
      [CartItemKeys.ItemId]: product.id,
      [CartItemKeys.ShopId]: product.shop_id ?? shop.id,
      [CartItemKeys.Additions]: additions,
      [CartItemKeys.Quantity]: 1,
    } satisfies Omit<ICartItem, 'id'>;
    return this.cartService
      .addOrUpdateCartItem$({
        newCartItem,
        quantity: 1,
        product,
      })
      .pipe(
        take(1),
        switchMap(() => EMPTY),
      );
  }

  /**
   * Adds the chosen simple gift on a new line. Matching the paid line would keep its catalog origin,
   * so the order would contain two paid units and no free one.
   */
  private addSimpleProductWithBogofIfEligible(
    product: DoodProductModel,
    shop: DoodShopModel,
    productOrigin: Extract<ProductOrigin, { channel: 'bogof' }>,
  ) {
    const suggestion = this.store.selectSignal(selectBogofData)()?.suggestions?.[0];
    const freeUnitsToAdd = suggestion ? nonComposableProductFreeBogofProductToAdd(suggestion) : 1;
    const freeItem: Omit<ICartItem, 'id'> = {
      [CartItemKeys.ItemId]: product.id,
      [CartItemKeys.ShopId]: product.shop_id ?? shop.id,
      [CartItemKeys.Additions]: [],
      [CartItemKeys.Quantity]: freeUnitsToAdd,
    };
    const forceOrigin: ProductOriginWithAddedValue = {
      ...productOrigin,
      added_value_cents: 0,
      metadata: {},
    };
    return this.cartService.addCartItem$(freeItem, forceOrigin).pipe(take(1), ignoreElements());
  }

  private navigateToKioskProduct(shop: DoodShopModel, product: { id: string }): void {
    this.navigateToShopPage(shop, {
      extraUrl: `product/${product.id}/product-add`,
    });
  }

  private openProductAddModal({
    product,
    shopId,
  }: {
    product: DoodProductModel;
    shopId: string;
  }): void {
    const _shopId = product[ProductKeys.ShopId] ?? shopId;
    if (!shopId) return;
    this.contentDispatcher.updateProduct({
      shopIsNotActive: false,
      shopId: _shopId,
      productId: product.id,
    });
    const isDigitalMenu = window.location.pathname.includes('/menu');
    this.modalsService.open(ProductAddModalComponent.handle, undefined, {
      allowAddToCart: !isDigitalMenu,
    });
  }

  private openKioskProductAddModal({
    product,
    shopId,
  }: {
    product: DoodProductModel;
    shopId: string;
  }): void {
    const _shopId = product[ProductKeys.ShopId] ?? shopId;
    if (!shopId) return;

    this.modalsService.open('kiosk-product-add-modal', undefined, {
      productId: product.id,
      shopId: _shopId,
    });
  }
}
